import { Box, Stack, Typography } from "@mui/material";
import { Duration, intervalToDuration } from "date-fns";
import { useContext } from "react";
import { useTranslation } from "react-i18next";
import { Anchors, routes } from "../../constants/routes";
import { GoogleServicesContext } from "../../context/GoogleServicesContext";
import siteImages from "../../data/siteImages";
import { gaAction, gaCategory } from "../../utils/analytics";
import { useGradient } from "../../utils/useGradient";
import { replaceParam } from "../../utils/utilities";
import { AnimatedView } from "../utils/AnimatedView";
import { ButtonSize, ButtonVariant } from "../utils/Button";
import NavButton from "../utils/NavButton";
import ImportantPartners from "./ImportantPartners";

const DEFAULT_YEARS = 15;
const FOUNDATION_DATE = "2008-08-01";

const AboutUs = () => {
  const { t } = useTranslation();
  const { analyticsEvent } = useContext(GoogleServicesContext);
  const gradient = useGradient("vertical");

  const title = t("home.about.title");
  const categories =
    (t("home.about.categories", { returnObjects: true }) as Array<{
      amount: string;
      title: string;
      description: string;
    }>) ?? [];
  const categoryImages = siteImages.aboutCategoryImages;

  const getCategoryNumber = (amount: string) => {
    if (amount.includes("{{yearsSinceFounded}}")) {
      const duration: Duration = intervalToDuration({
        start: new Date(FOUNDATION_DATE),
        end: new Date()
      });

      return duration.years
        ? replaceParam(amount, "yearsSinceFounded", duration.years.toString())
        : DEFAULT_YEARS;
    }

    return amount;
  };

  return (
    <Box
      id={Anchors.aboutUs}
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 2
      }}>
      <Typography variant="h4" component="h2" fontWeight="bold" mb={{ xs: 6, md: 8 }}>
        {title}
      </Typography>
      {categories.map((category, index: number) => (
        <Box
          key={index}
          sx={{
            display: "flex",
            flexDirection: {
              xs: "column",
              md: index % 2 === 0 ? "row" : "row-reverse"
            }
          }}>
          <AnimatedView animation={index % 2 === 0 ? "slideLeft" : "slideRight"}>
            <Box
              sx={{
                width: "100%",
                height: "100%",
                px: { xs: 0, md: 5 },
                py: { xs: 3, md: 5 },
                display: "flex",
                flexDirection: "column",
                justifyContent: "center"
              }}>
              <Stack
                direction="row"
                spacing={3}
                sx={{
                  alignItems: "center",
                  justifyContent: { xs: "center", md: "start" },
                  mb: 5
                }}>
                <Typography
                  variant="h1"
                  component="p"
                  fontWeight="bold"
                  sx={{
                    background: gradient,
                    backgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                    fontWeight: "bold"
                  }}>
                  {getCategoryNumber(category.amount)}
                </Typography>
                <Typography variant="h4" component="p">
                  {category.title}
                </Typography>
              </Stack>

              <Typography variant="body2">{category.description}</Typography>
            </Box>
          </AnimatedView>

          <AnimatedView animation={index % 2 === 0 ? "slideRight" : "slideLeft"}>
            <Box
              sx={{
                width: "100%",
                display: "flex",
                justifyContent: "center"
              }}>
              {categoryImages[index] && (
                <Box
                  component={"img"}
                  src={categoryImages[index].src}
                  alt={categoryImages[index].alt}
                  sx={(theme) => ({
                    borderRadius: theme.spacing(4),
                    boxShadow: "5px 5px 4px 0px rgba(0, 0, 0, 0.25)",
                    width: { xs: "100%", md: "80%" }
                  })}
                />
              )}
            </Box>
          </AnimatedView>
        </Box>
      ))}
      <NavButton
        route={routes.partner}
        onClick={() => analyticsEvent(gaCategory.partner, gaAction.click)}
        label={t("buttons.becomePartner")}
        variant={ButtonVariant.primary}
        size={ButtonSize.full}
        sx={{
          width: "18em",
          my: 10
        }}
      />

      <AnimatedView>
        <ImportantPartners />
      </AnimatedView>
    </Box>
  );
};

export default AboutUs;
